/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import type { PersonnelConfig } from '../../../../shared/types/config';

interface AddPersonnelModalProps {
  showAddModal: boolean;
  setShowAddModal: React.Dispatch<React.SetStateAction<boolean>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  loadingConfigs: boolean;
  personnelConfigs: PersonnelConfig[];
  isRecruiting: boolean;
  recruitError: string | null;
  recruitSuccess: string | null;
  availableSlots: number;
  handleRecruit: (config: PersonnelConfig, quantity: number) => void;
}

const AddPersonnelModal: React.FC<AddPersonnelModalProps> = ({
  showAddModal,
  setShowAddModal,
  searchQuery,
  setSearchQuery,
  loadingConfigs,
  personnelConfigs,
  isRecruiting,
  recruitError,
  recruitSuccess,
  availableSlots,
  handleRecruit,
}) => {
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  const getQty = (id: number) => quantities[id] ?? 1;

  const setQty = (id: number, value: number) => {
    const clamped = Math.max(1, Math.min(value, availableSlots > 0 ? availableSlots : 99));
    setQuantities(prev => ({ ...prev, [id]: clamped }));
  };

  if (!showAddModal) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={() => {
        setShowAddModal(false);
        setSearchQuery('');
        setQuantities({});
      }}
    >
      <div
        className="bg-zinc-900 rounded-lg shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden border border-zinc-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-zinc-700 bg-zinc-800/50">
          <div>
            <h2 className="text-2xl font-bold text-white">Ajouter du personnel</h2>
            {availableSlots > 0 ? (
              <p className="text-sm text-zinc-400 mt-1">
                <span className="text-green-400 font-semibold">{availableSlots}</span> place{availableSlots > 1 ? 's' : ''} disponible{availableSlots > 1 ? 's' : ''}
              </p>
            ) : (
              <p className="text-sm text-red-400 mt-1">Bâtiment complet — aucune place disponible</p>
            )}
          </div>
          <button
            onClick={() => {
              setShowAddModal(false);
              setQuantities({});
            }}
            className="text-zinc-400 hover:text-white transition-colors p-2 hover:bg-zinc-700 rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {recruitError && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">
              {recruitError}
            </div>
          )}
          {recruitSuccess && (
            <div className="mb-4 p-3 bg-green-500/20 border border-green-500 rounded-lg text-green-400 text-sm">
              {recruitSuccess}
            </div>
          )}

          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un grade..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-2.5 w-5 h-5 text-zinc-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[calc(80vh-260px)]">
            {loadingConfigs ? (
              <div className="text-center py-12 text-zinc-400">
                Chargement des configurations...
              </div>
            ) : (() => {
              const filteredConfigs = personnelConfigs
                .filter(config =>
                  config.grade.toLowerCase().includes(searchQuery.toLowerCase().trim())
                )
                .sort((a, b) => (a.ordre ?? 999) - (b.ordre ?? 999));

              if (filteredConfigs.length === 0) {
                return (
                  <div className="text-center py-12 text-zinc-400">
                    <p className="text-lg mb-2">
                      {searchQuery ? 'Aucun résultat trouvé' : 'Aucune configuration disponible'}
                    </p>
                    <p className="text-sm">
                      {searchQuery
                        ? 'Essayez de modifier votre recherche'
                        : 'Aucune configuration de personnel trouvée pour ce service'}
                    </p>
                  </div>
                );
              }

              return (
                <>
                  <div className="text-sm text-zinc-400 mb-3">
                    {filteredConfigs.length} configuration{filteredConfigs.length > 1 ? 's' : ''} trouvée{filteredConfigs.length > 1 ? 's' : ''}
                    {searchQuery && ` pour "${searchQuery}"`}
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {filteredConfigs.map((config) => {
                      const id = config.id!;
                      const qty = getQty(id);
                      const totalCost = config.prix * qty;
                      const noSlots = availableSlots <= 0;
                      const disabled = isRecruiting || noSlots;

                      return (
                        <div
                          key={id}
                          className={`bg-zinc-800 border rounded-lg p-4 transition-colors ${noSlots ? 'border-red-800/50 opacity-60' : 'border-zinc-700 hover:border-sky-500'}`}
                        >
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-lg font-semibold text-white">{config.grade}</h4>
                              <div className="flex items-center gap-3 mt-2 flex-wrap">
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                                  {config.prix}€ / agent
                                </span>
                                {qty > 1 && (
                                  <span className="px-2 py-1 bg-sky-500/20 text-sky-400 rounded text-xs">
                                    Total : {totalCost}€
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              {/* Sélecteur de quantité */}
                              <div className="flex items-center border border-zinc-600 rounded-lg overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() => setQty(id, qty - 1)}
                                  disabled={disabled || qty <= 1}
                                  className="px-2 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm font-bold"
                                >
                                  −
                                </button>
                                <input
                                  type="number"
                                  min={1}
                                  max={availableSlots > 0 ? availableSlots : 99}
                                  value={qty}
                                  onChange={(e) => setQty(id, parseInt(e.target.value) || 1)}
                                  disabled={disabled}
                                  className="w-12 text-center bg-zinc-800 text-white text-sm py-1.5 focus:outline-none disabled:opacity-40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => setQty(id, qty + 1)}
                                  disabled={disabled || qty >= availableSlots}
                                  className="px-2 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm font-bold"
                                >
                                  +
                                </button>
                              </div>

                              <button
                                onClick={() => handleRecruit(config, qty)}
                                disabled={disabled}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-600 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                              >
                                {isRecruiting ? 'Recrutement...' : `Recruter${qty > 1 ? ` ×${qty}` : ''}`}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddPersonnelModal;
